import React, { useRef, useState, useEffect, useCallback } from 'react';
import { motion, useInView, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import styled, { createGlobalStyle } from 'styled-components';

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Montserrat:wght@600;700;800&display=swap');

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html, body {
    margin: 0;
    padding: 0;
    height: 100%;
    width: 100%;
    background: #030b1a;
  }

  body {
    position: relative;
    min-height: 100vh;
    font-family: 'Inter', 'Segoe UI', Tahoma, sans-serif;
    color: #e6f2ff;
    line-height: 1.6;
    background:
      radial-gradient(120% 120% at 10% 10%, rgba(36, 167, 255, 0.18), transparent 60%),
      radial-gradient(100% 100% at 90% 0%, rgba(18, 214, 214, 0.15), transparent 70%),
      linear-gradient(135deg, #020817 0%, #03142b 45%, #043f63 100%);
    overflow-x: hidden;
  }

  #root {
    min-height: 100vh;
    width: 100%;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: 'Montserrat', 'Inter', 'Segoe UI', Tahoma, sans-serif;
    font-weight: 700;
    line-height: 1.2;
    color: #f8fbff;
  }

  p {
    color: inherit;
  }

  a {
    color: inherit;
    text-decoration: none;
  }
`;

const ACCENT_PRIMARY = '#22d3ee';
const ACCENT_SECONDARY = '#5efbf2';
const SURFACE_ALPHA = 'rgba(8, 16, 32, 0.82)';
const HERO_GALLERY_IMAGES = [
  '/images/New Images/Gallery/Pooltablephotography-26.jpg',
  '/images/New Images/Gallery/Pooltablephotography-27.jpg',
  '/images/New Images/Gallery/Pooltablephotography-28.jpg',
  '/images/New Images/Gallery/Pooltablephotography-29.jpg',
  '/images/New Images/Gallery/Pooltablephotography-30.jpg',
  '/images/New Images/Gallery/Pooltablephotography-31.jpg',
  '/images/New Images/Gallery/Pooltablephotography-32.jpg',
  '/images/New Images/Gallery/Pooltablephotography-33.jpg'
];
const TRIANGLE_HOLDER_IMAGES = [
  '/images/New Images/Triangle Holder 1.jpg',
  '/images/New Images/Triangle Holder 2.jpg',
  '/images/New Images/Triangle Holder 3.jpg'
];
const CHROME_CORNER_IMAGES = [
  '/images/New Images/Chrome Corner 1.jpg',
  '/images/New Images/Chrome Corner 2.jpg',
  '/images/New Images/Chrome Corner 3.jpg'
];
const MIDDLE_POCKET_IMAGES = [
  '/images/New Images/Middle Pocket 1.jpg',
  '/images/New Images/Middle Pocket 2.jpg'
];
const LEG_IMAGES = [
  '/images/New Images/Leg 1.jpg',
  '/images/New Images/Leg 2.jpg'
];
const BLACK_IMAGES = [
  '/images/New Images/Colours/Black_7ft_1.jpg',
  '/images/New Images/Colours/Black_7ft_2.jpg',
  '/images/New Images/Colours/Black_7ft_3.jpg'
];
const STONE_IMAGES = [
  '/images/New Images/Colours/Stone_7ft_1.jpg',
  '/images/New Images/Colours/Stone_7ft_2.jpg',
  '/images/New Images/Colours/Stone_7ft_3.jpg',
  '/images/New Images/Colours/Stone_7ft_4.jpg'
];
const GREY_OAK_IMAGES = [
  '/images/New Images/Colours/Grey_Oak_7ft_1.jpg',
  '/images/New Images/Colours/Grey_Oak_7ft_2.jpg',
  '/images/New Images/Colours/Grey_Oak_7ft_3.jpg',
  '/images/New Images/Colours/Grey_Oak_7ft_4.jpg'
];
const RUSTIC_OAK_IMAGES = [
  '/images/New Images/Colours/Rustic_oak_7ft_1.jpg',
  '/images/New Images/Colours/Rustic_oak_7ft_2.jpg',
  '/images/New Images/Colours/Rustic_oak_7ft_3.jpg',
  '/images/New Images/Colours/Rustic_oak_7ft_4.jpg'
];
const RUSTIC_BLACK_IMAGES = [
  '/images/New Images/Colours/Rustic_black_7ft_1.jpg',
  '/images/New Images/Colours/Rustic_black_7ft_2.jpg',
  '/images/New Images/Colours/Rustic_black_7ft_3.jpg',
  '/images/New Images/Colours/Rustic_black_7ft_4.jpg'
];
const CONSTRUCTION_IMAGES = [
  '/images/New Images/Construction/Pooltablephotography-102.jpg',
  '/images/New Images/Construction/Pooltablephotography-103.jpg',
  '/images/New Images/Construction/Pooltablephotography-104.jpg',
  '/images/New Images/Construction/Pooltablephotography-105.jpg',
  '/images/New Images/Construction/Pooltablephotography-106.jpg',
  '/images/New Images/Construction/Pooltablephotography-107.jpg',
  '/images/New Images/Construction/Pooltablephotography-108.jpg',
  '/images/New Images/Construction/Pooltablephotography-109.jpg',
  '/images/New Images/Construction/Pooltablephotography-110.jpg',
  '/images/New Images/Construction/Pooltablephotography-111.jpg',
  '/images/New Images/Construction/Pooltablephotography-112.jpg',
  '/images/New Images/Construction/Pooltablephotography-113.jpg',
  '/images/New Images/Construction/Pooltablephotography-114.jpg',
  '/images/New Images/Construction/Pooltablephotography-115.jpg',
  '/images/New Images/Construction/Pooltablephotography-116.jpg',
  '/images/New Images/Construction/Pooltablephotography-117.jpg',
  '/images/New Images/Construction/Pooltablephotography-118.jpg',
  '/images/New Images/Construction/Pooltablephotography-119.jpg',
  '/images/New Images/Construction/Pooltablephotography-120.jpg',
  '/images/New Images/Construction/Pooltablephotography-121.jpg'
];

const CONTACT_TO = 'hello@englishpooltableexports.com';
const CONTACT_SUBJECT_PREFIX = 'Atlas Pro enquiry';
const MIN_TIME_BEFORE_SUBMIT_MS = 2000;
const SUBMIT_COOLDOWN_MS = 10000;

const createEmptyContactForm = () => ({
  name: '',
  email: '',
  company: '',
  phone: '',
  message: '',
  website: ''
});

const createCaptchaChallenge = () => {
  const firstNumber = Math.floor(Math.random() * 8) + 2;
  const secondNumber = Math.floor(Math.random() * 8) + 1;
  return {
    prompt: `${firstNumber} + ${secondNumber}`,
    answer: firstNumber + secondNumber
  };
};

const escapeHtml = (value = '') =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

// Archived feature content retained for potential future use
const ARCHIVED_FEATURE_CONTENT = {
  playingSurface: {
    heading: 'Professional-Grade Playing Surface',
    paragraphs: [
      'Experience perfect playability with our precision-ground slate bed, ensuring a consistently level playing area for accurate ball roll and enhanced gameplay.',
      'Choose from a range of customizable cloth colors to match your personal style or existing decor, while maintaining professional-grade performance.'
    ],
    bullets: [
      'Precision-ground slate playing surface',
      'Customizable cloth color options',
      'Consistently level playing area',
      'Professional-grade ball response'
    ],
    image: {
      src: '/images/signature-champion-premium-edition-pool-playing-surface.JPG',
      alt: 'Premium Playing Surface'
    }
  },
  cushions: {
    heading: 'Professional-Grade Cushions',
    paragraphs: [
      'Equipped with high-response cushion rubber that provides consistent and predictable ball rebound, meeting professional standards.',
      'Our innovative cushion system ensures perfect angles and reliable performance, essential for both casual play and serious competition.'
    ],
    bullets: [
      'High-response cushion rubber',
      'Consistent ball rebound',
      'Professional-grade performance',
      'Perfect angle consistency'
    ],
    image: {
      src: '/images/signature-champion-premium-edition-pool-cushion-rail.JPG',
      alt: 'Professional-Grade Cushions'
    }
  }
};

const LandingContainer = styled.div`
  width: 100%;
  color: #e6f2ff;
  min-height: 100vh;
  position: relative;
  padding-bottom: 6rem;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  overscroll-behavior-y: contain;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(65% 65% at 20% 20%, rgba(94, 251, 242, 0.07), transparent 60%),
      radial-gradient(90% 90% at 100% 0%, rgba(34, 211, 238, 0.05), transparent 70%);
    pointer-events: none;
    z-index: 0;
  }
`;

const HeroSection = styled(motion.section)`
  position: relative;
  width: 100%;
  padding: clamp(3.5rem, 7vw, 5.75rem) clamp(1.6rem, 4.5vw, 3.5rem) clamp(2.5rem, 6vw, 4rem);
  display: flex;
  justify-content: center;
  align-items: center;
  background:
    radial-gradient(85% 95% at 15% 10%, rgba(34, 211, 238, 0.15), transparent 65%),
    radial-gradient(90% 80% at 90% 15%, rgba(12, 90, 255, 0.12), transparent 70%),
    linear-gradient(135deg, rgba(2, 8, 23, 0.95) 0%, rgba(3, 18, 40, 0.82) 45%, rgba(6, 28, 60, 0.76) 100%);
  overflow: visible;

  @media (max-width: 768px) {
    padding: clamp(2.5rem, 8vw, 3.75rem) clamp(1rem, 6vw, 1.8rem) clamp(2.5rem, 9vw, 3.2rem);
  }
`;

const HeroBackdrop = styled(motion.figure)`
  width: min(1600px, 100%);
  margin: 0;
  position: relative;
  border-radius: clamp(26px, 4vw, 40px);
  overflow: hidden;
  box-shadow:
    0 50px 140px rgba(4, 16, 34, 0.6),
    0 0 0 1px rgba(94, 251, 242, 0.15);
  background: rgba(2, 10, 23, 0.85);

  @media (max-width: 768px) {
    border-radius: clamp(22px, 6vw, 28px);
  }
`;

const HeroBackdropImage = styled(motion.img)`
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
  transform: scale(1.01);
`;

const HeroBackdropOverlay = styled.div`
  position: absolute;
  inset: 0;
  background:
    linear-gradient(120deg, rgba(3, 12, 28, 0.82) 0%, rgba(3, 19, 40, 0.55) 42%, rgba(34, 211, 238, 0.18) 100%),
    radial-gradient(80% 60% at 50% 25%, rgba(34, 211, 238, 0.25), transparent 70%);
  pointer-events: none;
`;

const SiteHeader = styled(motion.header)`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: clamp(1.8rem, 5vw, 3.2rem) clamp(1.5rem, 5vw, 3.75rem) clamp(1.1rem, 4vw, 2rem);
  position: relative;
  z-index: 5;
  background: rgba(2, 12, 26, 0.75);
  backdrop-filter: blur(24px);
  box-shadow: 0 34px 110px rgba(4, 16, 34, 0.55);
`;

const HeaderInner = styled.div`
  width: min(1280px, 100%);
  display: flex;
  flex-direction: column;
  gap: clamp(1.25rem, 3vw, 1.9rem);
`;

const HeaderBrandRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: clamp(1.1rem, 3vw, 2.4rem);

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1.2rem;
  }
`;

const NavContainer = styled(motion.nav)`
  width: 100%;
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: clamp(1rem, 3vw, 2.2rem);
  padding: clamp(0.9rem, 2.5vw, 1.2rem) clamp(1.4rem, 3vw, 2.1rem);
  border-radius: 999px;
  background: linear-gradient(130deg, rgba(3, 18, 36, 0.92), rgba(2, 11, 24, 0.78));
  border: 1px solid rgba(94, 251, 242, 0.2);
  box-shadow: 0 26px 80px rgba(4, 16, 34, 0.5), inset 0 0 0 1px rgba(255, 255, 255, 0.05);
  overflow: hidden;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: stretch;
    border-radius: 28px;
  }
`;

const NavBrandText = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
`;

const NavBrandPrimary = styled.span`
  font-weight: 800;
  letter-spacing: 0.08em;
  font-size: clamp(1.05rem, 1.7vw, 1.35rem);
  text-transform: uppercase;
  background: linear-gradient(90deg, #f8fbff 0%, rgba(94, 251, 242, 0.9) 100%);
  -webkit-background-clip: text;
  color: transparent;
`;

const NavBrandSecondary = styled.span`
  font-size: clamp(0.68rem, 1vw, 0.78rem);
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: rgba(226, 243, 255, 0.66);
`;

const NavBrand = styled.button`
  border: none;
  background: transparent;
  color: #f8fbff;
  font-family: 'Montserrat', 'Inter', 'Segoe UI', Tahoma, sans-serif;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.35rem 0;
  transition: transform 0.3s ease;

  &:focus-visible {
    outline: 2px solid rgba(94, 251, 242, 0.6);
    outline-offset: 4px;
  }

  &:hover {
    transform: translateY(-2px);
  }

  &:hover ${NavBrandSecondary} {
    color: ${ACCENT_SECONDARY};
  }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(0.9rem, 2.5vw, 2rem);

  @media (max-width: 900px) {
    justify-content: center;
  }

  @media (max-width: 540px) {
    flex-direction: column;
    width: 100%;
  }
`;

const NavLink = styled.button`
  border: none;
  background: rgba(255, 255, 255, 0.05);
  color: rgba(226, 243, 255, 0.88);
  text-transform: uppercase;
  letter-spacing: 0.22em;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 0.6rem 1.45rem;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  border-radius: 999px;
  transition: color 0.3s ease, transform 0.3s ease, background 0.3s ease, box-shadow 0.3s ease;
  box-shadow: inset 0 0 0 1px rgba(94, 251, 242, 0.12);

  &::before {
    content: '';
    position: absolute;
    left: clamp(0.7rem, 1.2vw, 1rem);
    top: 50%;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${ACCENT_SECONDARY};
    transform: translate(-150%, -50%) scale(0);
    transition: transform 0.3s ease;
    box-shadow: 0 0 14px rgba(94, 251, 242, 0.7);
  }

  &:hover {
    color: ${ACCENT_SECONDARY};
    background: rgba(94, 251, 242, 0.08);
    transform: translateY(-2px);
    box-shadow: 0 18px 45px rgba(34, 211, 238, 0.25);
  }

  &:hover::before,
  &:focus-visible::before {
    transform: translate(-50%, -50%) scale(1);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(94, 251, 242, 0.6), 0 18px 45px rgba(34, 211, 238, 0.25);
  }

  @media (max-width: 900px) {
    width: auto;
  }

  @media (max-width: 540px) {
    width: 100%;
    text-align: center;
  }
`;

const NavActionButton = styled.button`
  border: none;
  background: linear-gradient(120deg, rgba(34, 211, 238, 0.9) 0%, rgba(94, 251, 242, 0.7) 55%, rgba(223, 255, 255, 1) 100%);
  color: #021227;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  font-size: 0.78rem;
  font-weight: 700;
  padding: 0.65rem 2rem;
  border-radius: 999px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: 0 20px 60px rgba(34, 211, 238, 0.4);
  transition: transform 0.3s ease, box-shadow 0.3s ease, filter 0.3s ease;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.35);
    opacity: 0.6;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 26px 70px rgba(34, 211, 238, 0.5);
    filter: brightness(1.06);
  }

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.7);
    outline-offset: 4px;
  }

  @media (max-width: 900px) {
    width: 100%;
  }
`;

const HeroIntroSection = styled(motion.section)`
  position: relative;
  width: 100%;
  padding: clamp(2.5rem, 6vw, 4rem) 0 clamp(4rem, 10vw, 6.5rem);
  display: flex;
  justify-content: center;
  margin-top: clamp(-5rem, -7vw, -3rem);
  z-index: 2;

  @media (max-width: 768px) {
    margin-top: clamp(-3rem, -10vw, -2rem);
    padding: clamp(2rem, 8vw, 3.5rem) 0 clamp(3.5rem, 12vw, 5.5rem);
  }
`;

const HeroShowcase = styled(motion.div)`
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
  gap: clamp(1.1rem, 3.2vw, 1.6rem);
  padding: 0 clamp(1rem, 2.6vw, 1.4rem);
  align-items: stretch;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const HeroInfoCard = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
  color: #e8f6ff;
  padding: clamp(1.8rem, 4vw, 2.4rem) clamp(1rem, 2.5vw, 1.4rem);
  border-radius: 24px;
  border: 1px solid rgba(94, 251, 242, 0.12);
  background: rgba(5, 12, 26, 0.62);
  backdrop-filter: blur(18px);
  box-shadow: 0 40px 110px rgba(4, 16, 34, 0.48);
  transform: translateZ(40px);

  @media (max-width: 1024px) {
    transform: none;
  }
`;

const HeroMediaCard = styled(motion.div)`
  position: relative;
  border-radius: 24px;
  background: rgba(3, 12, 26, 0.65);
  border: 1px solid rgba(94, 251, 242, 0.16);
  box-shadow: 0 40px 110px rgba(4, 16, 34, 0.5);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  padding: clamp(0.7rem, 1.5vw, 1rem);
`;

const HeroHighlights = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 0.9rem;
  width: 100%;
`;

const HeroHighlight = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  font-size: clamp(0.95rem, 1.25vw, 1.1rem);
  line-height: 1.65;
  color: rgba(223, 238, 255, 0.85);

  &::before {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: linear-gradient(135deg, ${ACCENT_PRIMARY}, ${ACCENT_SECONDARY});
    box-shadow: 0 0 16px rgba(94, 251, 242, 0.65);
    margin-top: 0.45rem;
    flex-shrink: 0;
  }
`;
const HeroGalleryViewport = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 10;
  border-radius: 20px;
  overflow: hidden;
`;

const HeroGalleryImage = styled(motion.img)`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const HeroGalleryNavButton = styled(motion.button)`
  position: absolute;
  top: 50%;
  ${(props) => (props.$position === 'left' ? 'left: 1rem;' : 'right: 1rem;')}
  transform: translateY(-50%);
  width: 46px;
  height: 46px;
  border-radius: 50%;
  border: 1px solid rgba(94, 251, 242, 0.3);
  background: rgba(4, 16, 34, 0.6);
  color: rgba(223, 238, 255, 0.9);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 14px 32px rgba(4, 16, 34, 0.55);
  & > span {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transform: translateY(-1px);
  }


  &:hover {
    transform: translateY(-50%) scale(1.05);
    background: rgba(34, 211, 238, 0.22);
    box-shadow: 0 20px 45px rgba(34, 211, 238, 0.28);
  }

  &:active {
    transform: translateY(-50%) scale(1.02);
  }

  &:focus-visible {
    outline: 2px solid rgba(94, 251, 242, 0.6);
    outline-offset: 4px;
  }
`;

const HeroGalleryMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.2rem;
  gap: 1.5rem;
  color: rgba(223, 238, 255, 0.75);
  font-size: 0.9rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
`;

const HeroGalleryPagination = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin: 0 auto;
`;

const HeroGalleryDot = styled.button`
  width: ${(props) => (props.$active ? '28px' : '18px')};
  height: 6px;
  border-radius: 999px;
  border: none;
  padding: 0;
  background: ${(props) =>
    props.$active
      ? `linear-gradient(120deg, ${ACCENT_PRIMARY}, ${ACCENT_SECONDARY})`
      : 'rgba(223, 238, 255, 0.25)'};
  box-shadow: ${(props) => (props.$active ? '0 0 16px rgba(94, 251, 242, 0.5)' : 'none')};
  cursor: pointer;
  transition: all 0.3s ease;

  &:focus-visible {
    outline: 2px solid rgba(94, 251, 242, 0.6);
    outline-offset: 3px;
  }
`;

const Title = styled(motion.h1)`
  font-size: clamp(2.8rem, 6vw, 5.5rem);
  font-weight: 800;
  line-height: 1.08;
  text-align: left;
  width: 100%;
  transform: translateZ(50px);
  letter-spacing: -0.02em;
  background: linear-gradient(135deg, #ffffff 0%, #d8ebff 45%, ${ACCENT_SECONDARY} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  color: #f2f8ff;
  text-shadow: 0 18px 48px rgba(34, 211, 238, 0.3);
  
  @media (max-width: 1024px) {
    transform: none;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: clamp(1.1rem, 1.9vw, 1.6rem);
  font-weight: 600;
  letter-spacing: 0.38rem;
  text-transform: uppercase;
  color: rgba(226, 244, 255, 0.78);
  position: relative;
  padding-bottom: 0.5rem;
  transform: translateZ(30px);
  text-align: left;
  margin: 0;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    transform: none;
    width: 120px;
    height: 2px;
    background: linear-gradient(90deg, rgba(34, 211, 238, 0), ${ACCENT_SECONDARY}, rgba(34, 211, 238, 0));
  }
  
  @media (max-width: 768px) {
    letter-spacing: 0.25rem;
    padding: 0 0.5rem 0.5rem;
    transform: none;
  }
`;

const HeroDescription = styled(motion.p)`
  font-size: clamp(1rem, 1.3vw, 1.2rem);
  max-width: 100%;
  line-height: 1.85;
  color: rgba(223, 238, 255, 0.82);
  transform: translateZ(20px);
  margin: 0;
  background: rgba(3, 12, 28, 0.55);
  border-radius: 18px;
  padding: 1.4rem 1.6rem;
  border: 1px solid rgba(94, 251, 242, 0.12);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.03);
  
  @media (max-width: 1024px) {
    transform: none;
  }

  @media (max-width: 768px) {
    padding: 1.3rem 1.4rem;
  }
`;

const HeroBadge = styled(motion.span)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding: 0.65rem 1.6rem;
  border-radius: 999px;
  background: rgba(34, 211, 238, 0.12);
  border: 1px solid rgba(34, 211, 238, 0.45);
  color: rgba(224, 244, 255, 0.85);
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  box-shadow: 0 12px 30px rgba(34, 211, 238, 0.28);
  backdrop-filter: blur(8px);

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${ACCENT_SECONDARY};
    box-shadow: 0 0 12px rgba(94, 251, 242, 0.8);
  }
`;

const HeroActions = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 1rem;
  width: 100%;
  margin-top: 0.5rem;
`;

const PrimaryButton = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding: 0.95rem 2.6rem;
  border-radius: 999px;
  border: none;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: linear-gradient(135deg, ${ACCENT_PRIMARY} 0%, ${ACCENT_SECONDARY} 100%);
  color: #041021;
  cursor: pointer;
  box-shadow: 0 22px 48px rgba(34, 211, 238, 0.35);
  transition: transform 0.3s ease, box-shadow 0.3s ease, filter 0.3s ease;
  position: relative;

  &:hover {
    transform: translateY(-3px) scale(1.01);
    box-shadow: 0 26px 60px rgba(34, 211, 238, 0.4);
    filter: brightness(1.05);
  }

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.65);
    outline-offset: 3px;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SecondaryButton = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding: 0.95rem 2.6rem;
  border-radius: 999px;
  border: 1px solid rgba(226, 243, 255, 0.35);
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(226, 243, 255, 0.85);
  background: rgba(8, 18, 38, 0.6);
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease, background 0.3s ease;
  backdrop-filter: blur(10px);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 16px 40px rgba(3, 12, 28, 0.45);
    border-color: rgba(94, 251, 242, 0.55);
    background: rgba(8, 18, 38, 0.75);
  }

  &:focus-visible {
    outline: 2px solid rgba(94, 251, 242, 0.7);
    outline-offset: 3px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const FeatureSection = styled(motion.section)`
  width: 100%;
  max-width: 1300px;
  margin: 0 auto 4rem;
  padding: clamp(4.25rem, 6vw, 5.5rem) clamp(2rem, 5vw, 3.5rem);
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: clamp(2.75rem, 4.5vw, 3.75rem);
  align-items: center;
  position: relative;
  z-index: 2;
  color: #e8f6ff;
  background: ${SURFACE_ALPHA};
  border-radius: 28px;
  border: 1px solid rgba(94, 251, 242, 0.12);
  box-shadow: 0 38px 120px rgba(4, 16, 34, 0.45);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(34, 211, 238, 0.14) 0%, rgba(4, 16, 34, 0) 60%);
    opacity: 0.7;
    pointer-events: none;
  }

  @media (max-width: 1024px) {
    padding: clamp(3.5rem, 7vw, 4.25rem) clamp(1.5rem, 5vw, 2.5rem);
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
  
  @media (max-width: 768px) {
    padding: 3.25rem 1.5rem;
    border-radius: 24px;
    margin-bottom: 3rem;
  }
`;

const FeatureVisual = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  text-align: center;
`;

const FeatureCarouselWrapper = styled(motion.div)`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  align-items: stretch;
`;

const FeatureCarouselViewport = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 5 / 3;
  border-radius: 22px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid rgba(94, 251, 242, 0.16);
  background: rgba(3, 12, 26, 0.65);
  box-shadow: 0 32px 90px rgba(4, 16, 34, 0.48);

  @media (max-width: 768px) {
    aspect-ratio: 16 / 10;
  }
`;

const FeatureCarouselImage = styled(motion.img)`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const FeatureCarouselNavButton = styled(motion.button)`
  position: absolute;
  top: 50%;
  ${(props) => (props.$position === 'left' ? 'left: 1rem;' : 'right: 1rem;')}
  transform: translateY(-50%);
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 1px solid rgba(94, 251, 242, 0.35);
  background: rgba(4, 16, 34, 0.62);
  color: rgba(226, 243, 255, 0.9);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 16px 38px rgba(4, 16, 34, 0.5);
  transition: transform 0.3s ease, background 0.3s ease, box-shadow 0.3s ease;
  line-height: 1;
  padding: clamp(0.9rem, 2vw, 1.3rem);
  transform-origin: center;

  & > span {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transform: translateY(-1px);
  }

  &:hover {
    transform: translateY(-50%) scale(1.05);
    background: rgba(34, 211, 238, 0.22);
    box-shadow: 0 22px 48px rgba(34, 211, 238, 0.25);
  }

  &:active {
    transform: translateY(-50%) scale(1.02);
  }

  &:focus-visible {
    outline: 2px solid rgba(94, 251, 242, 0.65);
    outline-offset: 4px;
  }
`;


const CarouselNavIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.35rem;
  line-height: 1;
  font-weight: 600;
`;

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;
const FeatureCarouselMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.2rem;
  padding: 0 0.35rem;
  font-size: 0.78rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(223, 238, 255, 0.72);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.8rem;
  }
`;

const FeatureCarouselDots = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin: 0.35rem auto 0;
`;

const FeatureCarouselDot = styled.button`
  width: ${(props) => (props.$active ? '26px' : '16px')};
  height: 6px;
  border-radius: 999px;
  border: none;
  padding: 0;
  cursor: pointer;
  background: ${(props) =>
    props.$active
      ? `linear-gradient(120deg, ${ACCENT_PRIMARY}, ${ACCENT_SECONDARY})`
      : 'rgba(223, 238, 255, 0.28)'};
  box-shadow: ${(props) => (props.$active ? '0 0 14px rgba(94, 251, 242, 0.45)' : 'none')};
  transition: all 0.3s ease;

  ${FeatureCarouselWrapper} & {
    background: ${(props) =>
      props.$active
        ? `linear-gradient(120deg, ${ACCENT_PRIMARY}, ${ACCENT_SECONDARY})`
        : 'rgba(223, 238, 255, 0.28)'};
  }

  &:focus-visible {
    outline: 2px solid rgba(94, 251, 242, 0.6);
    outline-offset: 3px;
  }
`;

const FeatureCarousel = ({
  images = [],
  altPrefix = 'Gallery image',
  onOpenGallery,
  autoAdvance = true,
  showMeta = true,
  showDots = true,
  showNavigation = true,
  initialIndex = 0,
  ...wrapperProps
}) => {
  const validImages = Array.isArray(images) ? images.filter(Boolean) : [];
  const totalImages = validImages.length;
  const [activeIndex, setActiveIndex] = useState(initialIndex);

  useEffect(() => {
    if (initialIndex >= 0 && initialIndex < totalImages) {
      setActiveIndex(initialIndex);
    }
  }, [initialIndex, totalImages]);

  useEffect(() => {
    if (!autoAdvance || totalImages <= 1) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setActiveIndex((prev) => (prev + 1) % totalImages);
    }, 7000);

    return () => clearTimeout(timer);
  }, [autoAdvance, totalImages, activeIndex]);

  useEffect(() => {
    if (totalImages === 0) {
      setActiveIndex(0);
    } else if (activeIndex >= totalImages) {
      setActiveIndex(0);
    }
  }, [totalImages, activeIndex]);

  if (totalImages === 0) {
    return null;
  }

  const handlePrev = () => {
    setActiveIndex((prev) => (prev - 1 + totalImages) % totalImages);
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev + 1) % totalImages);
  };

  const handleSelect = (index) => {
    setActiveIndex(index);
  };

  const handleOpenGallery = () => {
    if (onOpenGallery) {
      onOpenGallery(validImages, activeIndex);
    }
  };

  return (
    <FeatureCarouselWrapper {...wrapperProps}>
      <FeatureCarouselViewport onClick={handleOpenGallery}>
        <AnimatePresence mode="wait">
          <FeatureCarouselImage
            key={validImages[activeIndex]}
            src={validImages[activeIndex]}
            alt={`${altPrefix} ${activeIndex + 1}`}
            initial={{ opacity: 0, scale: 1.03 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.45, ease: 'easeOut' }}
          />
        </AnimatePresence>
        {showNavigation && totalImages > 1 && (
          <>
            <FeatureCarouselNavButton
              type="button"
              $position="left"
              onClick={(event) => {
                event.stopPropagation();
                handlePrev();
              }}
                whileHover={{ scale: 1.05 }}
                aria-label="Show previous image"
              >
                <CarouselNavIcon aria-hidden="true">&lsaquo;</CarouselNavIcon>
              <VisuallyHidden>Show previous image</VisuallyHidden>
            </FeatureCarouselNavButton>
            <FeatureCarouselNavButton
              type="button"
              $position="right"
              onClick={(event) => {
                event.stopPropagation();
                handleNext();
              }}
                whileHover={{ scale: 1.05 }}
                aria-label="Show next image"
              >
                <CarouselNavIcon aria-hidden="true">&rsaquo;</CarouselNavIcon>
              <VisuallyHidden>Show next image</VisuallyHidden>
            </FeatureCarouselNavButton>
          </>
        )}
      </FeatureCarouselViewport>
      {showMeta && totalImages > 1 && (
        <FeatureCarouselMeta>
          <span>{altPrefix}</span>
          {showDots && (
            <FeatureCarouselDots>
              {validImages.map((imageSrc, index) => (
                <FeatureCarouselDot
                  key={imageSrc}
                  type="button"
                  $active={index === activeIndex}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleSelect(index);
                  }}
                  aria-label={`Show ${altPrefix} image ${index + 1}`}
                />
              ))}
            </FeatureCarouselDots>
          )}
          <span>{`${activeIndex + 1} / ${totalImages}`}</span>
        </FeatureCarouselMeta>
      )}
      {!showMeta && showDots && totalImages > 1 && (
        <FeatureCarouselDots>
          {validImages.map((imageSrc, index) => (
            <FeatureCarouselDot
              key={imageSrc}
              type="button"
              $active={index === activeIndex}
              onClick={(event) => {
                event.stopPropagation();
                handleSelect(index);
              }}
              aria-label={`Show ${altPrefix} image ${index + 1}`}
            />
          ))}
        </FeatureCarouselDots>
      )}
    </FeatureCarouselWrapper>
  );
};

const FeatureGalleryHint = styled.span`
  font-size: 0.78rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(223, 238, 255, 0.72);
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${ACCENT_SECONDARY};
    box-shadow: 0 0 12px rgba(94, 251, 242, 0.7);
  }
`;

const FeatureContent = styled(motion.div)`
  h2 {
    font-size: clamp(2rem, 3.4vw, 2.6rem);
    margin-bottom: 1.5rem;
    color: #f3f9ff;
    position: relative;
    padding-bottom: 1rem;
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 120px;
      height: 3px;
      background: linear-gradient(90deg, rgba(34, 211, 238, 0.8), rgba(34, 211, 238, 0));
    }
    
    @media (max-width: 768px) {
      font-size: 2.1rem;
    }
  }
  
  p {
    font-size: 1.15rem;
    line-height: 1.8;
    margin-bottom: 1rem;
    color: rgba(223, 238, 255, 0.82);
    
    @media (max-width: 768px) {
      font-size: 1.1rem;
    }
  }

  ul {
    list-style: none;
    padding: 0;
    margin-top: 1.5rem;
    
    li {
      font-size: 1.05rem;
      margin-bottom: 0.9rem;
      padding-left: 1.8rem;
      position: relative;
      color: rgba(226, 243, 255, 0.88);
      
      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0.55rem;
        width: 0.55rem;
        height: 0.55rem;
        border-radius: 50%;
        background: ${ACCENT_SECONDARY};
        box-shadow: 0 0 12px rgba(94, 251, 242, 0.75);
      }
    }
  }
`;

const FeatureGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2.2rem;
  padding: clamp(1.2rem, 3vw, 1.8rem);
  max-width: 1300px;
  margin: 4rem auto 0;
  position: relative;
  z-index: 2;
  
  @media (max-width: 1024px) {
    padding: 2rem;
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    margin-top: 3rem;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    padding: 1.25rem 1rem;
    margin-top: 2.5rem;
    gap: 1.5rem;
  }
`;

const FeaturesWrapper = styled(motion.section)`
  width: 100%;
  max-width: 100%;
  margin: 0 auto 4rem;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: clamp(2.5rem, 6vw, 4rem);

  @media (max-width: 768px) {
    margin-bottom: 3rem;
  }
`;

const FeaturesHeader = styled(motion.div)`
  text-align: center;
  max-width: 780px;
  margin: 0 auto;
  padding: 0 clamp(1rem, 3vw, 1.4rem);
  display: flex;
  flex-direction: column;
  gap: 1rem;

  h2 {
    font-size: clamp(2.4rem, 4vw, 3.2rem);
    margin: 0;
    color: #f4fbff;
    letter-spacing: -0.015em;
  }

  p {
    color: rgba(223, 238, 255, 0.78);
    font-size: clamp(1rem, 1.5vw, 1.2rem);
    margin: 0 auto;
  }
`;

const SectionTitle = styled(motion.div)`
  width: 100%;
  max-width: 1240px;
  margin: 0 auto 3rem;
  padding: 0 clamp(2rem, 6vw, 3.5rem);
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  h2 {
    font-size: clamp(2.3rem, 3.8vw, 3rem);
    margin: 0;
    color: #f4fbff;
    letter-spacing: -0.012em;
  }

  p {
    color: rgba(223, 238, 255, 0.74);
    font-size: clamp(1rem, 1.4vw, 1.18rem);
    margin: 0 auto;
    max-width: 760px;
  }
`;

const FeatureCard = styled(motion.div)`
  background: rgba(5, 14, 28, 0.72);
  padding: clamp(2.6rem, 5vw, 3.2rem) clamp(2.2rem, 4.8vw, 3rem);
  border-radius: 22px;
  text-align: center;
  color: rgba(226, 243, 255, 0.9);
  border: 1px solid rgba(94, 251, 242, 0.12);
  box-shadow: 0 26px 70px rgba(3, 12, 28, 0.48);
  backdrop-filter: blur(14px);
  overflow: hidden;
  
  img {
    width: 100%;
    height: 200px;
    object-fit: cover;
    border-radius: 14px;
    margin-bottom: 1.4rem;
    border: 1px solid rgba(94, 251, 242, 0.15);
    box-shadow: 0 16px 40px rgba(3, 12, 28, 0.45);
  }

  ${FeatureCarouselWrapper} {
    margin-bottom: 1.4rem;
  }

  h3 {
    font-size: 1.4rem;
    margin-bottom: 0.75rem;
    color: #f3f9ff;
  }

  p {
    font-size: 1.05rem;
    line-height: 1.7;
    color: rgba(223, 238, 255, 0.82);
  }
`;

const FeatureImage = styled.img`
  width: 100%;
  aspect-ratio: 4 / 3;
  border-radius: 18px;
  object-fit: cover;
  border: 1px solid rgba(94, 251, 242, 0.2);
  margin-bottom: 1.5rem;
`;

const FeatureImagePlaceholder = styled.div`
  width: 100%;
  aspect-ratio: 4 / 3;
  border-radius: 18px;
  border: 1px dashed rgba(94, 251, 242, 0.35);
  background: rgba(6, 18, 36, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(223, 238, 255, 0.65);
  font-size: 0.95rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  margin-bottom: 1.5rem;
`;

const RetailerSection = styled(motion.div)`
  text-align: center;
  padding: 5rem 2rem 6rem;
  margin-top: 3rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 380px;
  position: relative;
  z-index: 2;
  background: ${SURFACE_ALPHA};
  border-radius: 30px;
  border: 1px solid rgba(94, 251, 242, 0.1);
  box-shadow: 0 30px 100px rgba(4, 16, 34, 0.45);
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(65% 65% at 50% 20%, rgba(34, 211, 238, 0.18), transparent 72%);
    opacity: 0.7;
    pointer-events: none;
  }
  
  @media (max-width: 768px) {
    padding: 4rem 1.5rem 4.5rem;
    min-height: 320px;
    border-radius: 24px;
  }
`;

const RetailerLogo = styled(motion.img)`
  height: clamp(120px, 18vw, 180px);
  width: auto;
  margin-bottom: 3.5rem;
  object-fit: contain;
  filter: drop-shadow(0 18px 45px rgba(3, 12, 28, 0.55));
`;

const RetailerLink = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  border: none;
  cursor: pointer;
  padding: 1.15rem 2.8rem;
  border-radius: 999px;
  background: linear-gradient(135deg, ${ACCENT_PRIMARY} 0%, ${ACCENT_SECONDARY} 100%);
  color: #041021;
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  box-shadow: 0 26px 70px rgba(34, 211, 238, 0.35);
  transition: transform 0.35s ease, box-shadow 0.35s ease;
  margin-top: 1.5rem;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 32px 90px rgba(34, 211, 238, 0.42);
  }

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.7);
    outline-offset: 4px;
  }

  @media (max-width: 768px) {
    width: 100%;
    max-width: 320px;
    padding: 1.1rem 2rem;
    font-size: 0.95rem;
  }
`;


const ModalOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(2, 10, 20, 0.88);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 2rem;
  backdrop-filter: blur(6px);
`;

const ModalContent = styled(motion.div)`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  border-radius: 24px;
  background: rgba(5, 14, 28, 0.92);
  border: 1px solid rgba(94, 251, 242, 0.18);
  box-shadow: 0 30px 80px rgba(3, 12, 28, 0.65);
  
  img {
    max-width: 80vw;
    max-height: 80vh;
    object-fit: contain;
    border-radius: 18px;
    border: 1px solid rgba(94, 251, 242, 0.15);
    box-shadow: 0 24px 60px rgba(3, 12, 28, 0.55);
    display: block;
  }
`;

const CloseButton = styled(motion.button)`
  position: absolute;
  top: -18px;
  right: -18px;
  width: 42px;
  height: 42px;
  border-radius: 999px;
  border: 1px solid rgba(94, 251, 242, 0.45);
  background: rgba(3, 12, 28, 0.85);
  color: #f5faff;
  font-size: 1.1rem;
  font-weight: 700;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 16px 40px rgba(3, 12, 28, 0.55);
  transition: transform 0.3s ease, background 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    background: rgba(8, 20, 40, 0.9);
  }

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.65);
    outline-offset: 3px;
  }
  
  @media (max-width: 768px) {
    top: -14px;
    right: -14px;
  }
`;

const NarrativeSection = styled(motion.section)`
  width: 100%;
  padding: clamp(5rem, 8vw, 7rem) clamp(1.5rem, 5vw, 3.25rem);
  display: flex;
  justify-content: center;
  position: relative;
  background:
    radial-gradient(60% 60% at 15% 20%, rgba(34, 211, 238, 0.06), transparent 65%),
    radial-gradient(50% 50% at 85% 30%, rgba(12, 148, 255, 0.05), transparent 70%);
`;

const NarrativeCard = styled(motion.div)`
  width: 100%;
  max-width: 1300px;
  margin: 0 auto;
  padding: clamp(3.5rem, 6vw, 5rem) clamp(2.25rem, 4.5vw, 3.75rem);
  border-radius: 30px;
  background: rgba(4, 13, 28, 0.82);
  border: 1px solid rgba(94, 251, 242, 0.18);
  box-shadow: 0 44px 140px rgba(3, 12, 28, 0.6);
  backdrop-filter: blur(22px);
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: clamp(1.35rem, 2vw, 1.9rem);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(140deg, rgba(34, 211, 238, 0.14), rgba(4, 16, 34, 0.1) 55%, rgba(4, 13, 28, 0.55) 100%);
    opacity: 0.85;
    pointer-events: none;
  }

  > * {
    position: relative;
    z-index: 2;
  }

  @media (max-width: 768px) {
    padding: clamp(2.75rem, 8vw, 3.5rem) clamp(1.4rem, 6vw, 2rem);
    border-radius: 26px;
    gap: 1.25rem;
  }
`;

const NarrativeLabel = styled.span`
  font-size: 0.78rem;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: rgba(223, 238, 255, 0.6);
`;

const NarrativeHeading = styled(motion.h2)`
  font-size: clamp(2.3rem, 4.2vw, 3.3rem);
  margin: 0;
  color: #f4fbff;
  letter-spacing: -0.01em;
`;

const NarrativeParagraph = styled(motion.p)`
  font-size: clamp(1rem, 1.4vw, 1.2rem);
  margin: 0 auto;
  max-width: min(860px, 100%);
  color: rgba(223, 238, 255, 0.78);
  line-height: 1.85;
`;

const ContactForm = styled(motion.form)`
  width: 100%;
  max-width: 860px;
  margin: 2.5rem auto 0;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  text-align: left;
  position: relative;
`;

const ContactFieldGrid = styled.div`
  display: grid;
  width: 100%;
  gap: 1.4rem;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
`;

const ContactField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  ${(props) => (props.$fullWidth ? 'grid-column: 1 / -1;' : '')}
`;

const ContactLabel = styled.label`
  font-size: 0.82rem;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: rgba(223, 238, 255, 0.65);
`;

const contactInputSurface = `
  width: 100%;
  border-radius: 16px;
  border: 1px solid rgba(94, 251, 242, 0.18);
  background: rgba(3, 12, 28, 0.85);
  color: #f8fbff;
  padding: 1rem 1.15rem;
  font-size: 1rem;
  font-family: 'Inter', 'Segoe UI', Tahoma, sans-serif;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  box-shadow: 0 18px 40px rgba(3, 12, 28, 0.35);
  
  &::placeholder {
    color: rgba(223, 238, 255, 0.45);
    letter-spacing: 0.04em;
  }
  
  &:focus {
    outline: none;
    border-color: ${ACCENT_PRIMARY};
    background: rgba(6, 20, 38, 0.95);
    box-shadow: 0 0 0 2px rgba(34, 211, 238, 0.22);
  }
`;

const ContactInput = styled.input`
  ${contactInputSurface}
`;

const ContactTextarea = styled.textarea`
  ${contactInputSurface}
  min-height: 160px;
  resize: vertical;
`;

const CaptchaChallenge = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.85rem;
  flex-wrap: wrap;
  background: rgba(4, 13, 28, 0.85);
  border: 1px solid rgba(94, 251, 242, 0.22);
  border-radius: 16px;
  padding: 0.85rem 1.25rem;
`;

const CaptchaTextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;

  span:first-child {
    font-size: 0.72rem;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: rgba(223, 238, 255, 0.6);
  }

  span:last-child {
    font-size: 1.35rem;
    letter-spacing: 0.12em;
    font-weight: 700;
    color: #f8fbff;
  }
`;

const CaptchaRefreshButton = styled.button`
  border: none;
  background: none;
  color: ${ACCENT_SECONDARY};
  font-size: 0.78rem;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  font-weight: 700;
  cursor: pointer;
  padding: 0.4rem 0.2rem;
  transition: color 0.3s ease;

  &:hover {
    color: ${ACCENT_PRIMARY};
  }

  &:focus-visible {
    outline: 2px solid rgba(94, 251, 242, 0.6);
    outline-offset: 4px;
  }
`;

const ContactActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.2rem;
  flex-wrap: wrap;
`;

const ContactSubmitButton = styled(motion.button)`
  border: none;
  border-radius: 999px;
  padding: 1.15rem 2.75rem;
  background: linear-gradient(135deg, ${ACCENT_PRIMARY} 0%, ${ACCENT_SECONDARY} 100%);
  color: #041021;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  cursor: pointer;
  box-shadow: 0 28px 70px rgba(34, 211, 238, 0.35);
  transition: opacity 0.3s ease, box-shadow 0.3s ease;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    box-shadow: none;
  }
`;

const ContactStatus = styled.p`
  margin: 0;
  min-height: 1.4rem;
  font-size: 0.95rem;
  letter-spacing: 0.05em;
  flex: 1 1 240px;
  text-align: left;
  color: ${({ $variant }) => {
    if ($variant === 'error') {
      return '#ff9fae';
    }
    if ($variant === 'success') {
      return '#5efbf2';
    }
    if ($variant === 'submitting') {
      return '#c8e7ff';
    }
    return 'rgba(223, 238, 255, 0.74)';
  }};
`;

const ContactFootnote = styled.p`
  margin: 0;
  text-align: center;
  font-size: 0.85rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(223, 238, 255, 0.5);
`;

const HoneypotWrapper = styled.div`
  position: absolute;
  left: -10000px;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
`;
const ModalNavButton = styled(motion.button)`
  position: absolute;
  top: 50%;
  ${(props) => (props.$position === 'left' ? 'left: -22px;' : 'right: -22px;')}
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid rgba(94, 251, 242, 0.45);
  background: rgba(3, 14, 28, 0.82);
  color: #f5faff;
  font-size: 1.35rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 18px 45px rgba(3, 12, 28, 0.6);
  transition: transform 0.3s ease, background 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-50%) scale(1.05);
    background: rgba(8, 24, 42, 0.9);
  }

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.7);
    outline-offset: 3px;
  }

  @media (max-width: 768px) {
    ${(props) => (props.$position === 'left' ? 'left: -12px;' : 'right: -12px;')}
    width: 40px;
    height: 40px;
  }
`;

const ModalImageCounter = styled.div`
  position: absolute;
  bottom: 14px;
  left: 50%;
  transform: translateX(-50%);
  padding: 0.4rem 0.9rem;
  border-radius: 999px;
  background: rgba(3, 14, 28, 0.78);
  border: 1px solid rgba(94, 251, 242, 0.35);
  color: rgba(233, 244, 255, 0.85);
  font-size: 0.82rem;
  letter-spacing: 0.18em;
`;

const ColorShowcase = styled(motion.section)`
  padding: 7rem clamp(2rem, 6vw, 3.5rem) 6.5rem;
  position: relative;
  z-index: 2;
  min-height: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2.5rem;
  color: #e8f6ff;
  background: ${SURFACE_ALPHA};
  border-radius: 32px;
  border: 1px solid rgba(94, 251, 242, 0.1);
  box-shadow: 0 34px 110px rgba(4, 16, 34, 0.45);
  max-width: 1300px;
  margin: 0 auto 4rem;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(80% 80% at 50% 0%, rgba(34, 211, 238, 0.16), transparent 65%);
    opacity: 0.7;
    pointer-events: none;
  }
  
  h2 {
    position: relative;
    z-index: 3;
    text-align: center;
    font-size: clamp(2.2rem, 3.6vw, 2.8rem);
    margin-bottom: 1.5rem;
    opacity: 1 !important;
    width: 100%;
    color: #f5faff;
  }

  p {
    position: relative;
    z-index: 3;
    text-align: center;
    font-size: 1.15rem;
    line-height: 1.8;
    max-width: 780px;
    margin: 0 auto 3.5rem;
    color: rgba(223, 238, 255, 0.82);
  }
  
  @media (max-width: 1024px) {
    padding: 6rem 2rem 5.5rem;
  }
  
  @media (max-width: 768px) {
    padding: 4.5rem 1.5rem 4.5rem;
    border-radius: 26px;
    gap: 2rem;
    
    h2 {
      margin-bottom: 1.25rem;
      font-size: 2.2rem;
    }

    p {
      font-size: 1.05rem;
      margin-bottom: 3rem;
    }
  }
`;

const ColorGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2.2rem;
  width: 100%;
  max-width: 1300px;
  margin: 0 auto;
  padding: 0 clamp(1.1rem, 3vw, 1.6rem);
  position: relative;
  z-index: 3;
  
  @media (max-width: 1024px) {
    gap: 1.7rem;
  }
  
  @media (max-width: 768px) {
    padding: 0 1rem;
    gap: 1.3rem;
  }
`;
const ColorCard = styled(motion.div)`
  position: relative;
  border-radius: 18px;
  overflow: hidden;
  cursor: pointer;
  aspect-ratio: 16/11;
  transform-style: preserve-3d;
  perspective: 1000px;
  touch-action: pan-y;
  width: 100%;
  height: 100%;
  border: 1px solid rgba(94, 251, 242, 0.15);
  box-shadow: 0 24px 70px rgba(3, 12, 28, 0.4);
  background: rgba(3, 12, 28, 0.65);
  
  ${FeatureCarouselWrapper} {
    height: 100%;
  }

  ${FeatureCarouselViewport} {
    height: 100%;
  }
  
  &:hover {
    box-shadow: 0 30px 90px rgba(3, 12, 28, 0.55);
  }

  &:hover ${FeatureCarouselImage} {
    transform: scale(1.06);
    filter: saturate(1.05);
  }
`;

const ColorOverlay = styled(motion.div)`
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(3, 12, 28, 0) 0%, rgba(3, 12, 28, 0.2) 45%, rgba(3, 12, 28, 0.68) 85%, rgba(3, 12, 28, 0.85) 100%);
  padding: 1.2rem 1.4rem;
  color: #f5faff;
  z-index: 2;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 0.35rem;
  
  h3 {
    font-size: 1.05rem;
    margin: 0;
    font-weight: 600;
    letter-spacing: 0.04em;
  }
  
  p {
    font-size: 0.85rem;
    margin: 0;
    color: rgba(223, 238, 255, 0.74);
  }
`;

const AtlasProLanding = () => {
  const heroRef = useRef(null);
  const featureAnchorRef = useRef(null);
  const colorShowcaseRef = useRef(null);
  const aboutSectionRef = useRef(null);
  const contactSectionRef = useRef(null);
  const formMountedAtRef = useRef(Date.now());
  const lastContactSubmitRef = useRef(0);
  const isHeroInView = useInView(heroRef, { once: true });
  const { scrollYProgress } = useScroll();
  const [modalState, setModalState] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [heroGalleryIndex, setHeroGalleryIndex] = useState(0);
  const [contactForm, setContactForm] = useState(() => createEmptyContactForm());
  const [captchaChallenge, setCaptchaChallenge] = useState(() => createCaptchaChallenge());
  const [captchaInput, setCaptchaInput] = useState('');
  const [contactStatus, setContactStatus] = useState({ state: 'idle', message: '' });
  const heroGalleryLength = HERO_GALLERY_IMAGES.length;
  const heroGalleryImage = HERO_GALLERY_IMAGES[heroGalleryIndex];
  const isSubmitting = contactStatus.state === 'submitting';
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Enhanced parallax effects
  const heroY = useTransform(scrollYProgress, [0, 1], [0, -200]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.5], [1, 1.1]);

  const scrollToSection = useCallback((sectionRef) => {
    if (sectionRef?.current) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const handleScrollToFeatureOverview = useCallback(() => {
    scrollToSection(featureAnchorRef);
  }, [scrollToSection, featureAnchorRef]);

  const handleHeroGalleryNext = useCallback(() => {
    setHeroGalleryIndex((prev) => (prev + 1) % heroGalleryLength);
  }, [heroGalleryLength]);

  const handleHeroGalleryPrev = useCallback(() => {
    setHeroGalleryIndex((prev) => (prev - 1 + heroGalleryLength) % heroGalleryLength);
  }, [heroGalleryLength]);

  const handleHeroGallerySelect = useCallback((index) => {
    setHeroGalleryIndex(index);
  }, []);

  const handleNavClick = useCallback((sectionRef) => {
    scrollToSection(sectionRef);
  }, [scrollToSection]);

  const handleContactInputChange = useCallback((event) => {
    const { name, value } = event.target;
    setContactForm((prev) => ({ ...prev, [name]: value }));
    setContactStatus((prev) => (prev.state === 'idle' ? prev : { state: 'idle', message: '' }));
  }, []);

  const handleCaptchaInputChange = useCallback((event) => {
    setCaptchaInput(event.target.value);
    setContactStatus((prev) => (prev.state === 'idle' ? prev : { state: 'idle', message: '' }));
  }, []);

  const handleCaptchaRefresh = useCallback(() => {
    setCaptchaChallenge(createCaptchaChallenge());
    setCaptchaInput('');
    setContactStatus((prev) => (prev.state === 'idle' ? prev : { state: 'idle', message: '' }));
  }, []);

  const handleContactSubmit = useCallback(async (event) => {
    event.preventDefault();
    if (isSubmitting) {
      return;
    }

    const payload = {
      name: contactForm.name.trim(),
      email: contactForm.email.trim(),
      company: contactForm.company.trim(),
      phone: contactForm.phone.trim(),
      message: contactForm.message.trim()
    };

    if (!payload.name || !payload.email || !payload.message) {
      setContactStatus({
        state: 'error',
        message: 'Please share your name, email and a short message so we know how to help.'
      });
      return;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(payload.email)) {
      setContactStatus({
        state: 'error',
        message: 'Enter a valid email address so we can reply.'
      });
      return;
    }

    if (payload.message.length < 10) {
      setContactStatus({
        state: 'error',
        message: 'Tell us a little more about your requirements (10+ characters).'
      });
      return;
    }

    const captchaValue = parseInt(captchaInput, 10);
    if (Number.isNaN(captchaValue) || captchaValue !== captchaChallenge.answer) {
      setContactStatus({
        state: 'error',
        message: 'Captcha answer is incorrect. Please try again.'
      });
      setCaptchaChallenge(createCaptchaChallenge());
      setCaptchaInput('');
      return;
    }

    try {
      const now = Date.now();
      if (now - formMountedAtRef.current < MIN_TIME_BEFORE_SUBMIT_MS) {
        setContactStatus({
          state: 'error',
          message: 'Please take a moment to complete the form before sending.'
        });
        return;
      }

      if (lastContactSubmitRef.current && now - lastContactSubmitRef.current < SUBMIT_COOLDOWN_MS) {
        const waitSeconds = Math.ceil((SUBMIT_COOLDOWN_MS - (now - lastContactSubmitRef.current)) / 1000);
        setContactStatus({
          state: 'error',
          message: `Please wait ${waitSeconds}s before sending another enquiry.`
        });
        return;
      }

      if (contactForm.website.trim()) {
        setContactStatus({
          state: 'success',
          message: 'Thanks - we will reply within one working day.'
        });
        setContactForm(createEmptyContactForm());
        setCaptchaChallenge(createCaptchaChallenge());
        setCaptchaInput('');
        lastContactSubmitRef.current = now;
        return;
      }

      setContactStatus({ state: 'submitting', message: 'Sending your enquiry...' });

      const htmlMessage = `
        <h2>New Atlas Pro enquiry</h2>
        <p><strong>Name:</strong> ${escapeHtml(payload.name)}</p>
        <p><strong>Email:</strong> ${escapeHtml(payload.email)}</p>
        <p><strong>Company/Territory:</strong> ${escapeHtml(payload.company || 'n/a')}</p>
        <p><strong>Phone:</strong> ${escapeHtml(payload.phone || 'n/a')}</p>
        <p><strong>Message:</strong></p>
        <p>${escapeHtml(payload.message).replace(/\n/g, '<br />')}</p>
      `;

      const textMessage = [
        'New Atlas Pro enquiry',
        `Name: ${payload.name}`,
        `Email: ${payload.email}`,
        `Company/Territory: ${payload.company || 'n/a'}`,
        `Phone: ${payload.phone || 'n/a'}`,
        'Message:',
        payload.message
      ].join('\n');

      const subjectSuffix = payload.name ? ` — ${payload.name}` : '';

      const emailPayload = {
        to: CONTACT_TO,
        subject: `${CONTACT_SUBJECT_PREFIX}${subjectSuffix}`,
        html: htmlMessage,
        text: textMessage,
        replyTo: payload.email
      };

      const response = await fetch('/api/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(emailPayload)
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(result?.error || result?.message || 'We could not send your message right now.');
      }

      setContactStatus({
        state: 'success',
        message: 'Thanks - we will reply within one working day.'
      });
      setContactForm(createEmptyContactForm());
      setCaptchaChallenge(createCaptchaChallenge());
      setCaptchaInput('');
      lastContactSubmitRef.current = now;
    } catch (error) {
      setContactStatus({
        state: 'error',
        message:
          error?.message ||
          'We could not send your message right now. Please email hello@englishpooltableexports.com.'
      });
    }
  }, [captchaChallenge, captchaInput, contactForm, isSubmitting]);
  
  // Smooth scroll animations for sections
  const sectionVariants = {
    hidden: { opacity: 0, y: 100 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 1,
        ease: "easeOut",
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  // Enhanced color grid animation
  const colorGridY = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const colorGridOpacity = useTransform(scrollYProgress, [0, 0.5, 1], [0, 1, 0]);

  const handleImageClick = useCallback((imageSrc) => {
    setModalState({ images: [imageSrc], index: 0 });
  }, []);

  const handleGalleryOpen = useCallback((images, startIndex = 0) => {
    if (!images?.length) {
      return;
    }
    setModalState({ images, index: startIndex });
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalState(null);
  }, []);

  const handlePrevImage = useCallback(() => {
    setModalState((prev) => {
      if (!prev || prev.images.length <= 1) {
        return prev;
      }
      const nextIndex = (prev.index - 1 + prev.images.length) % prev.images.length;
      return { ...prev, index: nextIndex };
    });
  }, []);

  const handleNextImage = useCallback(() => {
    setModalState((prev) => {
      if (!prev || prev.images.length <= 1) {
        return prev;
      }
      const nextIndex = (prev.index + 1) % prev.images.length;
      return { ...prev, index: nextIndex };
    });
  }, []);

  return (
    <>
      <GlobalStyle />
      <LandingContainer>
        <SiteHeader
          initial={{ opacity: 0, y: -24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        >
          <HeaderInner>
            <HeaderBrandRow>
              <NavBrand
                type="button"
                onClick={() => handleNavClick(heroRef)}
              >
                <NavBrandText>
                  <NavBrandPrimary>English Pool Table Exports</NavBrandPrimary>
                  <NavBrandSecondary>Precision Built Pool Tables</NavBrandSecondary>
                </NavBrandText>
              </NavBrand>
            </HeaderBrandRow>
            <NavContainer
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
            >
              <NavLinks>
                <NavLink type="button" onClick={() => handleNavClick(featureAnchorRef)}>
                  Atlas Pro
                </NavLink>
                <NavLink type="button" onClick={() => handleNavClick(aboutSectionRef)}>
                  About Us
                </NavLink>
                <NavLink type="button" onClick={() => handleNavClick(colorShowcaseRef)}>
                  Finishes
                </NavLink>
              </NavLinks>
              <NavActionButton type="button" onClick={() => handleNavClick(contactSectionRef)}>
                Contact Us
              </NavActionButton>
            </NavContainer>
          </HeaderInner>
        </SiteHeader>

        <HeroSection 
          ref={heroRef}
          style={{
            y: isMobile ? 0 : heroY,
            scale: isMobile ? 1 : heroScale,
            opacity: isMobile ? 1 : heroOpacity
          }}
        >
          <HeroBackdrop
            initial={{ opacity: 0, scale: 0.96, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut", delay: 0.2 }}
          >
            <HeroBackdropImage
              src="/images/New Images/hero-image-2.jpg"
              alt="Atlas Pro pool tables ready for wholesale partners"
              loading="eager"
            />
            <HeroBackdropOverlay />
          </HeroBackdrop>
        </HeroSection>

        <HeroIntroSection>
          <HeroShowcase
            initial={{ opacity: 0, y: 60 }}
            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, ease: "easeOut", delay: 0.3 }}
          >
            <HeroInfoCard
              initial={{ opacity: 0, x: -40 }}
              animate={isHeroInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, ease: "easeOut", delay: 0.5 }}
            >
              <Subtitle
                initial={{ opacity: 0, x: -20 }}
                animate={isHeroInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.7, ease: "easeOut", delay: 0.55 }}
              >
                Wholesale Partner Programme
              </Subtitle>
              <Title
                initial={{ opacity: 0, x: -25 }}
                animate={isHeroInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.75, ease: "easeOut", delay: 0.65 }}
              >
                Atlas Pro: precision-built English pool tables for export
              </Title>
              <HeroDescription
                initial={{ opacity: 0, x: -20 }}
                animate={isHeroInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.75, ease: "easeOut", delay: 0.75 }}
              >
                English Pool Table Exports equips premium leisure partners with Atlas Pro English pool tables that arrive showroom ready. Every build is CNC-cut, hand-finished, and supported with marketing assets and technical guidance for seamless deployment.
              </HeroDescription>
              <HeroHighlights>
                <HeroHighlight>Three Bristol production units delivering 60-100 Atlas Pro tables every month.</HeroHighlight>
                <HeroHighlight>Atlas Pro white-label options, export logistics, and aftercare crafted around wholesale partners.</HeroHighlight>
                <HeroHighlight>European distribution programme opens in 2026 - secure Atlas Pro priority allocation now.</HeroHighlight>
              </HeroHighlights>
              <HeroActions
                initial={{ opacity: 0, y: 30 }}
                animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, ease: "easeOut", delay: 0.9 }}
              >
                <PrimaryButton
                  type="button"
                  onClick={handleScrollToFeatureOverview}
                  whileHover={{ scale: 1.02 }}
                >
                  Explore Features
                </PrimaryButton>
                <SecondaryButton
                  type="button"
                  onClick={() => handleNavClick(contactSectionRef)}
                  whileHover={{ scale: 1.02 }}
                >
                  Talk To Us
                </SecondaryButton>
              </HeroActions>
            </HeroInfoCard>
            <HeroMediaCard
              initial={{ opacity: 0, x: 40 }}
              animate={isHeroInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.9, ease: "easeOut", delay: 0.6 }}
            >
              <HeroGalleryViewport>
                <AnimatePresence mode="wait">
                  <HeroGalleryImage
                    key={heroGalleryImage}
                    src={heroGalleryImage}
                    alt={"Precision-built pool table gallery image " + (heroGalleryIndex + 1)}
                    initial={{ opacity: 0, scale: 1.05 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                  />
                </AnimatePresence>
                <HeroGalleryNavButton
                  type="button"
                  $position="left"
                  onClick={handleHeroGalleryPrev}
                  whileHover={{ scale: 1.05 }}
                  aria-label="Previous gallery image"
                >
                  <CarouselNavIcon aria-hidden="true">&lsaquo;</CarouselNavIcon>
                  <VisuallyHidden>Previous gallery image</VisuallyHidden>
                </HeroGalleryNavButton>
                <HeroGalleryNavButton
                  type="button"
                  $position="right"
                  onClick={handleHeroGalleryNext}
                  whileHover={{ scale: 1.05 }}
                  aria-label="Next gallery image"
                >
                  <CarouselNavIcon aria-hidden="true">&rsaquo;</CarouselNavIcon>
                  <VisuallyHidden>Next gallery image</VisuallyHidden>
              </HeroGalleryNavButton>
            </HeroGalleryViewport>
            <HeroGalleryMeta>
              <HeroGalleryPagination>
                {HERO_GALLERY_IMAGES.map((imageSrc, index) => (
                  <HeroGalleryDot
                    key={imageSrc}
                    type="button"
                      onClick={() => handleHeroGallerySelect(index)}
                      $active={index === heroGalleryIndex}
                      aria-label={"View gallery image " + (index + 1)}
                    />
                  ))}
                </HeroGalleryPagination>
                <span>{heroGalleryIndex + 1} / {heroGalleryLength}</span>
              </HeroGalleryMeta>
            </HeroMediaCard>
          </HeroShowcase>
        </HeroIntroSection>

        <FeaturesWrapper
          ref={featureAnchorRef}
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          <FeaturesHeader variants={itemVariants}>
            <h2>Features</h2>
            <p>Discover the engineering that makes the Atlas Pro a standout export-ready table.</p>
          </FeaturesHeader>

          <FeatureSection variants={itemVariants}>
            <FeatureContent variants={itemVariants}>
              <h2>Innovative Ball Return System</h2>
              <p>Experience the Atlas Pro&apos;s blend of professional and pub-style play with our revolutionary ball return system. This is the first free-play table to feature a ball window, bringing the authentic pub pool experience to your home or venue.</p>
              <p>The system combines a white ball separator with a unique ball window display, allowing players to see the collected balls - a feature previously exclusive to coin-operated tables. This innovative design enhances the playing experience while maintaining professional-grade functionality.</p>
              <ul>
                <li>First free-play table with ball window display</li>
                <li>White ball separator for easy retrieval</li>
                <li>Authentic pub-style ball viewing experience</li>
                <li>Convenient ball release mechanism</li>
              </ul>
            </FeatureContent>
            <FeatureVisual variants={itemVariants} transition={{ duration: 0.3 }}>
              <FeatureCarousel
                variants={itemVariants}
                images={[
                  "/images/New Images/Ball Window Shot.jpg",
                  "/images/signature-champion-premium-edition-pool-ball-release-latch-2.JPG"
                ]}
                altPrefix="Ball Return System"
                onOpenGallery={(images, startIndex) => handleGalleryOpen(images, startIndex)}
              />
              <FeatureGalleryHint>Tap to open full gallery</FeatureGalleryHint>
            </FeatureVisual>
          </FeatureSection>

          <FeatureGrid variants={itemVariants}>
            <FeatureCard
              variants={itemVariants}
              whileHover={{ y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <img 
                src="/images/New Images/Cue Ball Seperator.jpg" 
                alt="Cue Ball Separator"
                onClick={() => handleImageClick("/images/New Images/Cue Ball Seperator.jpg")}
                style={{ cursor: 'pointer' }}
              />
              <h3>Cue Ball Separator</h3>
              <p>Easy identification and retrieval of the cue ball with our innovative separator system.</p>
            </FeatureCard>

            <FeatureCard
              variants={itemVariants}
              whileHover={{ y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <img 
                src="/images/signature-champion-premium-edition-pool-beading.JPG" 
                alt="Premium Beading"
                onClick={() => handleImageClick("/images/signature-champion-premium-edition-pool-beading.JPG")}
                style={{ cursor: 'pointer' }}
              />
              <h3>Premium Beading</h3>
              <p>Hand-finished beading adds a touch of elegance while protecting the table's edges.</p>
            </FeatureCard>

            <FeatureCard
              variants={itemVariants}
              whileHover={{ y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <FeatureCarousel
                images={TRIANGLE_HOLDER_IMAGES}
                altPrefix="Triangle Holder"
                onOpenGallery={handleGalleryOpen}
                showMeta={false}
                autoAdvance={false}
              />
              <h3>Triangle Holder</h3>
              <p>Convenient storage for the rack when not in use, keeping your game area organized.</p>
            </FeatureCard>

            <FeatureCard
              variants={itemVariants}
              whileHover={{ y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <FeatureCarousel
                images={CHROME_CORNER_IMAGES}
                altPrefix="Narrow Chrome Corners"
                onOpenGallery={handleGalleryOpen}
                showMeta={false}
                autoAdvance={false}
              />
              <h3>Narrow Chrome Corners</h3>
              <p>Sleek chrome corner detailing that keeps edges protected while matching modern interiors.</p>
            </FeatureCard>

            <FeatureCard
              variants={itemVariants}
              whileHover={{ y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <FeatureCarousel
                images={MIDDLE_POCKET_IMAGES}
                altPrefix="Middle Pocket"
                onOpenGallery={handleGalleryOpen}
                showMeta={false}
                autoAdvance={false}
              />
              <h3>Middle Pocket</h3>
              <p>We've redesigned the middle pocket to reduce the chances of balls bouncing out and keep more shots in play.</p>
            </FeatureCard>

            <FeatureCard
              variants={itemVariants}
              whileHover={{ y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <img 
                src="/images/New Images/Plywood Supports.jpg" 
                alt="Ply Wood Slate Supports"
                onClick={() => handleImageClick("/images/New Images/Plywood Supports.jpg")}
                style={{ cursor: 'pointer' }}
              />
              <h3>Ply Wood Slate Supports</h3>
              <p>Rigid ply supports cradle the slate more effectively than MDF, reducing bowing over time and preserving a level playfield.</p>
            </FeatureCard>

            <FeatureCard
              variants={itemVariants}
              whileHover={{ y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <FeatureCarousel
                images={LEG_IMAGES}
                altPrefix="Injection Moulded Leg"
                onOpenGallery={handleGalleryOpen}
                showMeta={false}
                autoAdvance={false}
              />
              <h3>Injection Moulded Leg</h3>
              <p>An all-new internal design delivers a stronger leg that lasts longer, finished with an adjustable chrome foot for precise leveling by spanner.</p>
            </FeatureCard>

            <FeatureCard
              variants={itemVariants}
              whileHover={{ y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <FeatureImagePlaceholder>
                Image Coming Soon
              </FeatureImagePlaceholder>
              <h3>Two Sizes</h3>
              <p>Choose between 6ft and 7ft models to suit your room without compromising the full-size playing experience.</p>
            </FeatureCard>

            <FeatureCard
              variants={itemVariants}
              whileHover={{ y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <FeatureImagePlaceholder>
                Image Coming Soon
              </FeatureImagePlaceholder>
              <h3>Felted or Unfelt Supply</h3>
              <p>Take delivery of fully finished tables or blank beds so you can stock one chassis, add the exact cloth colour and spec each customer wants, and carry far less pre-finished inventory.</p>
            </FeatureCard>

            <FeatureCard
              variants={itemVariants}
              whileHover={{ y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <FeatureImagePlaceholder>
                Image Coming Soon
              </FeatureImagePlaceholder>
              <h3>Standard Height</h3>
              <p>Set at the professional 32&quot; playing height, the Atlas Pro keeps every stance familiar and competition ready.</p>
            </FeatureCard>

            <FeatureCard
              variants={itemVariants}
              whileHover={{ y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <FeatureImage src="/images/New Images/Sound_Reducing.jpg" alt="Sound-reducing internal materials" loading="lazy" />
              <h3>Sound-Reducing Internal Materials</h3>
              <p>Acoustic treatments inside the return system soften impact noise, creating a quieter playing experience at home or in venue.</p>
            </FeatureCard>
          </FeatureGrid>
        </FeaturesWrapper>

        <ColorShowcase
          ref={colorShowcaseRef}
          as={motion.section}
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: {
                staggerChildren: 0.2,
                delayChildren: 0.3
              }
            }
          }}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          <motion.h2 
            variants={{
              hidden: { opacity: 0, y: 50 },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.8,
                  ease: [0.215, 0.610, 0.355, 1.000]
                }
              }
            }}
          >
            Available Colours
          </motion.h2>
          <motion.p
            variants={{
              hidden: { opacity: 0, y: 30 },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.8,
                  ease: [0.215, 0.610, 0.355, 1.000]
                }
              }
            }}
          >
            Choose from our stunning range of five premium finishes, each carefully selected to complement any space. From timeless Black to contemporary Grey Oak, every colour option maintains our exceptional quality standards while offering unique aesthetic appeal.
          </motion.p>
          <ColorGrid
            variants={{
              hidden: { opacity: 0, y: 100 },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 1,
                  ease: [0.215, 0.610, 0.355, 1.000]
                }
              }
            }}
            style={{
              y: isMobile ? 0 : colorGridY,
              opacity: isMobile ? 1 : colorGridOpacity
            }}
          >
            <ColorCard
              as={motion.div}
              variants={itemVariants}
              whileHover={{ y: -10 }}
              transition={{ duration: 0.3 }}
              onClick={() => handleGalleryOpen(BLACK_IMAGES)}
            >
              <FeatureCarousel
                images={BLACK_IMAGES}
                altPrefix="Black finish"
                onOpenGallery={handleGalleryOpen}
                showMeta={false}
                autoAdvance={false}
                style={{ height: '100%' }}
              />
              <ColorOverlay>
                <h3>Black</h3>
                <p>Timeless classic</p>
              </ColorOverlay>
            </ColorCard>

            <ColorCard
              as={motion.div}
              variants={itemVariants}
              whileHover={{ y: -10 }}
              transition={{ duration: 0.3 }}
              onClick={() => handleGalleryOpen(STONE_IMAGES)}
            >
              <FeatureCarousel
                images={STONE_IMAGES}
                altPrefix="Stone finish"
                onOpenGallery={handleGalleryOpen}
                showMeta={false}
                autoAdvance={false}
                style={{ height: '100%' }}
              />
              <ColorOverlay>
                <h3>Stone</h3>
                <p>Modern minimalist design</p>
              </ColorOverlay>
            </ColorCard>

            <ColorCard
              as={motion.div}
              variants={itemVariants}
              whileHover={{ y: -10 }}
              transition={{ duration: 0.3 }}
              onClick={() => handleGalleryOpen(GREY_OAK_IMAGES)}
            >
              <FeatureCarousel
                images={GREY_OAK_IMAGES}
                altPrefix="Grey Oak finish"
                onOpenGallery={handleGalleryOpen}
                showMeta={false}
                autoAdvance={false}
                style={{ height: '100%' }}
              />
              <ColorOverlay>
                <h3>Grey Oak</h3>
                <p>Contemporary elegance</p>
              </ColorOverlay>
            </ColorCard>

            <ColorCard
              as={motion.div}
              variants={itemVariants}
              whileHover={{ y: -10 }}
              transition={{ duration: 0.3 }}
              onClick={() => handleGalleryOpen(RUSTIC_OAK_IMAGES)}
            >
              <FeatureCarousel
                images={RUSTIC_OAK_IMAGES}
                altPrefix="Rustic Oak finish"
                onOpenGallery={handleGalleryOpen}
                showMeta={false}
                autoAdvance={false}
                style={{ height: '100%' }}
              />
              <ColorOverlay>
                <h3>Rustic Oak</h3>
                <p>Classic warmth</p>
              </ColorOverlay>
            </ColorCard>

            <ColorCard
              as={motion.div}
              variants={itemVariants}
              whileHover={{ y: -10 }}
              transition={{ duration: 0.3 }}
              onClick={() => handleGalleryOpen(RUSTIC_BLACK_IMAGES)}
            >
              <FeatureCarousel
                images={RUSTIC_BLACK_IMAGES}
                altPrefix="Rustic Black finish"
                onOpenGallery={handleGalleryOpen}
                showMeta={false}
                autoAdvance={false}
                style={{ height: '100%' }}
              />
              <ColorOverlay>
                <h3>Rustic Black</h3>
                <p>Rich sophistication</p>
              </ColorOverlay>
            </ColorCard>
          </ColorGrid>
        </ColorShowcase>

        <SectionTitle
          variants={itemVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          <h2>Robust Construction</h2>
        </SectionTitle>

        <FeatureSection
          as={motion.section}
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          <FeatureContent
            as={motion.div}
            variants={itemVariants}
          >
            <h2>Engineered for Commercial and Home Use</h2>
            <p>
              Built with precision in Bristol, UK, our tables are designed to perform flawlessly in busy commercial environments while maintaining the refined finish expected in premium home settings. Each table features a solid MDF body reinforced with plywood supports, providing exceptional strength, rigidity, and durability.
            </p>
            <p>
              An adjustable feet system ensures perfect leveling on any surface, guaranteeing consistent playing conditions throughout the table's lifetime. To make installation easier and longer-lasting, we glue in the threaded inserts that hold the adjustable feet so they stay secure and never work loose.
            </p>
            <p>
              Over years of refinement, we've developed a construction process that prioritises both performance and practicality:
            </p>
            <ul>
              <li>UK-made Protex latches - four per long side and two per short for superior clamping strength and a lasting fit.</li>
              <li>Optimised ball gullies, precision-trimmed to reduce noise and deliver a cleaner, more professional appearance.</li>
              <li>Laser-cut felt and carpet lining inside the ball return for quieter operation and a premium finish.</li>
              <li>3D-printed ball return components that evolve with each production run to improve smoothness and serviceability.</li>
              <li>Integrated leg storage that keeps all four legs secure inside the cabinet for safer, compact transport.</li>
              <li>Innovative bolt-through frame construction that delivers superior stability and long-term structural integrity.</li>
              <li>CNC precision cutting on every panel to ensure perfect alignment and consistency - nothing is cut by hand.</li>
              <li>Enhanced cushion attachment system using four bolts per cushion for greater strength, accuracy, and longevity.</li>
              <li>Comprehensive quality control checks, including detailed multi-point inspections before dispatch.</li>
              <li>Individual serialisation on every table for full traceability and after-sales support.</li>
            </ul>
            <p>
              Every element, from hardware choice to internal finishing, reflects our philosophy of continuous improvement and attention to detail.
            </p>
            <p>
              Whether it's for a high-traffic commercial venue or a dedicated games room at home, our tables are built to deliver lasting performance and a premium playing experience.
            </p>
          </FeatureContent>
          <FeatureVisual
            variants={itemVariants}
            transition={{ duration: 0.3 }}
          >
            <FeatureCarousel
              variants={itemVariants}
              images={CONSTRUCTION_IMAGES}
              altPrefix="Robust Construction sequence"
              onOpenGallery={(images, startIndex) => handleGalleryOpen(images, startIndex)}
            />
            <FeatureGalleryHint>Under construction - tap to explore the build gallery</FeatureGalleryHint>
          </FeatureVisual>
        </FeatureSection>

        <NarrativeSection
          ref={aboutSectionRef}
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          <NarrativeCard variants={itemVariants}>
            <NarrativeLabel>About Us</NarrativeLabel>
            <NarrativeHeading variants={itemVariants}>
              From Arcades to Pool Tables
            </NarrativeHeading>
            <NarrativeParagraph variants={itemVariants}>
              When we launched Bitcade in 2017, we set out to build the best custom arcade machines in the business. Bristol-born founder Jack England channelled a lifelong love of Pac-Man, Space Invaders, Street Fighter and Mortal Kombat into lifelike replicas that reimagined the golden age of arcades.
            </NarrativeParagraph>
            <NarrativeParagraph variants={itemVariants}>
              Five years later Bitcade had become the UK&apos;s largest producer of bespoke arcade cabinets, and that appetite for precision engineering pushed us toward a new challenge. In January 2024 we pivoted fully into crafting the Atlas Pro range under English Pool Table Exports, carrying across the same obsession with detail, CNC accuracy and hand-finished quality.
            </NarrativeParagraph>
            <NarrativeParagraph variants={itemVariants}>
              Today our seven-strong team works across three Bristol production units, capable of delivering 60&nbsp;-&nbsp;100 precision-built Atlas Pro tables each month for our UK partners. With the foundations set, we&apos;re gearing up to expand into Europe from 2026, bringing the Atlas Pro experience to an even wider audience.
            </NarrativeParagraph>
          </NarrativeCard>
        </NarrativeSection>

        <NarrativeSection
          ref={contactSectionRef}
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          <NarrativeCard variants={itemVariants}>
            <NarrativeLabel>Contact Us</NarrativeLabel>
            <NarrativeHeading variants={itemVariants}>
              Explore UK Export Opportunities
            </NarrativeHeading>
            <NarrativeParagraph variants={itemVariants}>
              Share a few details about your territory or project and we&apos;ll follow up with export-ready specs, lead times, and pricing within one working day.
            </NarrativeParagraph>
            <ContactForm
              variants={itemVariants}
              onSubmit={handleContactSubmit}
              noValidate
            >
              <HoneypotWrapper aria-hidden="true">
                <label htmlFor="contactWebsite">Company Website</label>
                <input
                  id="contactWebsite"
                  name="website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={contactForm.website}
                  onChange={handleContactInputChange}
                />
              </HoneypotWrapper>
              <ContactFieldGrid>
                <ContactField>
                  <ContactLabel htmlFor="contactName">Full Name*</ContactLabel>
                  <ContactInput
                    id="contactName"
                    name="name"
                    type="text"
                    placeholder="Alex from Leisure Co."
                    value={contactForm.name}
                    onChange={handleContactInputChange}
                    autoComplete="name"
                  />
                </ContactField>
                <ContactField>
                  <ContactLabel htmlFor="contactEmail">Work Email*</ContactLabel>
                  <ContactInput
                    id="contactEmail"
                    name="email"
                    type="email"
                    placeholder="you@business.com"
                    value={contactForm.email}
                    onChange={handleContactInputChange}
                    autoComplete="email"
                  />
                </ContactField>
                <ContactField>
                  <ContactLabel htmlFor="contactCompany">Company or Territory</ContactLabel>
                  <ContactInput
                    id="contactCompany"
                    name="company"
                    type="text"
                    placeholder="Home Leisure Direct / DACH"
                    value={contactForm.company}
                    onChange={handleContactInputChange}
                    autoComplete="organization"
                  />
                </ContactField>
                <ContactField>
                  <ContactLabel htmlFor="contactPhone">Phone / WhatsApp</ContactLabel>
                  <ContactInput
                    id="contactPhone"
                    name="phone"
                    type="tel"
                    inputMode="tel"
                    placeholder="+44 7700 900123"
                    value={contactForm.phone}
                    onChange={handleContactInputChange}
                    autoComplete="tel"
                  />
                </ContactField>
                <ContactField $fullWidth>
                  <ContactLabel htmlFor="contactMessage">Message*</ContactLabel>
                  <ContactTextarea
                    id="contactMessage"
                    name="message"
                    placeholder="Tell us about your volumes, finishes and launch timeline..."
                    value={contactForm.message}
                    onChange={handleContactInputChange}
                  />
                </ContactField>
                <ContactField $fullWidth>
                  <ContactLabel htmlFor="contactCaptcha">Verification</ContactLabel>
                  <CaptchaChallenge>
                    <CaptchaTextGroup>
                      <span>Solve</span>
                      <span>{captchaChallenge.prompt}</span>
                    </CaptchaTextGroup>
                    <CaptchaRefreshButton type="button" onClick={handleCaptchaRefresh}>
                      Refresh
                    </CaptchaRefreshButton>
                  </CaptchaChallenge>
                  <ContactInput
                    id="contactCaptcha"
                    name="captcha"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="Enter the total"
                    value={captchaInput}
                    onChange={handleCaptchaInputChange}
                    autoComplete="off"
                  />
                </ContactField>
              </ContactFieldGrid>
              <ContactActions>
                <ContactSubmitButton
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={isSubmitting ? undefined : { scale: 1.02 }}
                  whileTap={isSubmitting ? undefined : { scale: 0.98 }}
                  transition={{ duration: 0.25 }}
                >
                  {isSubmitting ? 'Sending...' : 'Send Trade Enquiry'}
                </ContactSubmitButton>
                {contactStatus.message && (
                  <ContactStatus
                    role="status"
                    aria-live="polite"
                    $variant={contactStatus.state}
                  >
                    {contactStatus.message}
                  </ContactStatus>
                )}
              </ContactActions>
              <ContactFootnote>
                Enquiries route straight to our export team via secure email and receive a reply within one working day.
              </ContactFootnote>
            </ContactForm>
          </NarrativeCard>
        </NarrativeSection>

        <RetailerSection
          as={motion.div}
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          <motion.h2 
            variants={itemVariants}
            style={{ 
              marginBottom: '2rem',
              fontSize: '3rem'
            }}
          >
            Available Across the UK
          </motion.h2>
          <motion.p
            variants={itemVariants}
            style={{
              marginTop: '-1rem',
              marginBottom: '2rem',
              fontSize: '1.05rem',
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'rgba(226, 243, 255, 0.75)'
            }}
          >
            Export partners wanted for our 2026 European expansion
          </motion.p>
          <RetailerLogo
            src="/images/HomeLeisureDirect-Logo (1).png"
            alt="Home Leisure Direct"
            variants={itemVariants}
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.3 }}
            style={{ maxWidth: '600px', margin: '0 auto' }}
          />
          <RetailerLink
            as="button"
            type="button"
            variants={itemVariants}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            transition={{ duration: 0.3 }}
            onClick={() => handleNavClick(contactSectionRef)}
          >
            Request Atlas Pro Trade Pricing
          </RetailerLink>
        </RetailerSection>

        <AnimatePresence>
          {modalState && (
            <ModalOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleCloseModal}
            >
              <ModalContent
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.3 }}
                onClick={(event) => event.stopPropagation()}
              >
                <CloseButton
                  onClick={(event) => {
                    event.stopPropagation();
                    handleCloseModal();
                  }}
                  whileHover={{ scale: 1.1 }}
                >
                  X
                </CloseButton>
                {modalState.images.length > 1 && (
                  <>
                    <ModalNavButton
                      $position="left"
                    onClick={(event) => {
                      event.stopPropagation();
                      handlePrevImage();
                    }}
                    whileHover={{ scale: 1.05 }}
                  >
                    {'<'}
                  </ModalNavButton>
                  <ModalNavButton
                    $position="right"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleNextImage();
                    }}
                    whileHover={{ scale: 1.05 }}
                  >
                    {'>'}
                  </ModalNavButton>
                    <ModalImageCounter>
                      {modalState.index + 1} / {modalState.images.length}
                    </ModalImageCounter>
                  </>
                )}
                <img
                  src={modalState.images[modalState.index]}
                  alt={`Full size view ${modalState.index + 1}`}
                />
              </ModalContent>
            </ModalOverlay>
          )}
        </AnimatePresence>
      </LandingContainer>
    </>
  );
};

export default AtlasProLanding;







